import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, RefreshCw, Send, Archive, Undo2, Loader2, Save, Bell } from 'lucide-react';
import AdminLayout from './AdminLayout';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Intl.supportedValuesOf lands in all modern browsers; fall back to a
// curated list for older WebViews.
const FALLBACK_TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Anchorage', 'Pacific/Honolulu', 'Europe/London', 'Europe/Paris',
  'Europe/Berlin', 'Africa/Lagos', 'Africa/Nairobi', 'Africa/Johannesburg',
  'Asia/Dubai', 'Asia/Jerusalem', 'Asia/Tokyo', 'Asia/Singapore', 'Asia/Hong_Kong',
  'Asia/Kolkata', 'Australia/Sydney', 'Pacific/Auckland',
];

function listTimezones() {
  try {
    const supported = Intl.supportedValuesOf?.('timeZone');
    if (Array.isArray(supported) && supported.length > 0) return supported;
  } catch { /* ignore */ }
  return FALLBACK_TIMEZONES;
}

export default function AdminDidYouKnowPage() {
  const apiBase = `${window.location.origin}/api`;
  const timezones = useMemo(listTimezones, []);

  // `original` reflects what's on the server; `draft` is the editable
  // form. The Save button is enabled only when they differ. This is
  // what fixes "saves the moment I type a different number".
  const [original, setOriginal] = useState(null);
  const [draft, setDraft] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [facts, setFacts] = useState([]);
  const [loadingFacts, setLoadingFacts] = useState(true);
  const [filter, setFilter] = useState('all');
  const [regenerating, setRegenerating] = useState(false);
  const [pushingId, setPushingId] = useState(null);
  const [testingPush, setTestingPush] = useState(false);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${apiBase}/admin/did-you-know/settings`, {
        withCredentials: true,
      });
      setOriginal(res.data);
      setDraft(res.data);
    } catch {
      toast.error('Failed to load settings');
    }
  };

  const fetchFacts = async (status = filter) => {
    setLoadingFacts(true);
    try {
      const res = await axios.get(`${apiBase}/admin/did-you-know/list?status=${status}&limit=100`, {
        withCredentials: true,
      });
      setFacts(res.data?.facts || []);
    } catch {
      toast.error('Failed to load facts');
    } finally {
      setLoadingFacts(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchFacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect unsaved changes by shallow-comparing fields.
  const dirty = useMemo(() => {
    if (!original || !draft) return false;
    const keys = ['schedule_mode', 'frequency_hours', 'push_time_local_hour',
      'push_minute', 'timezone', 'push_enabled'];
    if (keys.some((k) => original[k] !== draft[k])) return true;
    const oa = (original.weekdays || []).slice().sort().join(',');
    const da = (draft.weekdays || []).slice().sort().join(',');
    return oa !== da;
  }, [original, draft]);

  const updateDraft = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const toggleWeekday = (day) => {
    const cur = new Set(draft.weekdays || []);
    if (cur.has(day)) cur.delete(day); else cur.add(day);
    updateDraft({ weekdays: Array.from(cur).sort() });
  };

  const handleSave = async () => {
    if (!dirty) return;
    setSavingSettings(true);
    try {
      const payload = {
        schedule_mode: draft.schedule_mode,
        push_time_local_hour: draft.push_time_local_hour,
        push_minute: draft.push_minute,
        timezone: draft.timezone,
        weekdays: draft.weekdays,
        push_enabled: draft.push_enabled,
      };
      // Frequency hours is only meaningful in interval mode, but we
      // still send it so the backend can keep it in sync.
      if (draft.schedule_mode === 'interval') {
        payload.frequency_hours = draft.frequency_hours;
      }
      const res = await axios.put(
        `${apiBase}/admin/did-you-know/settings`,
        payload,
        { withCredentials: true }
      );
      setOriginal(res.data);
      setDraft(res.data);
      toast.success('Schedule saved');
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || 'unknown'}`);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRevert = () => {
    if (!original) return;
    setDraft(original);
    toast.info('Reverted unsaved changes');
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await axios.post(`${apiBase}/admin/did-you-know/regenerate`, {}, {
        withCredentials: true,
        timeout: 60000,
      });
      const newFact = res.data?.fact;
      if (newFact) {
        toast.success(`Generated: ${newFact.title}`);
        await fetchFacts(filter);
      }
    } catch (e) {
      toast.error(`Generation failed: ${e?.response?.data?.detail || 'unknown'}`);
    } finally {
      setRegenerating(false);
    }
  };

  const handleArchive = async (factId) => {
    try {
      await axios.post(`${apiBase}/admin/did-you-know/${factId}/archive`, {}, {
        withCredentials: true,
      });
      toast.success('Archived');
      fetchFacts(filter);
    } catch {
      toast.error('Archive failed');
    }
  };

  const handleRestore = async (factId) => {
    try {
      await axios.post(`${apiBase}/admin/did-you-know/${factId}/restore`, {}, {
        withCredentials: true,
      });
      toast.success('Restored');
      fetchFacts(filter);
    } catch {
      toast.error('Restore failed');
    }
  };

  const handlePush = async (factId) => {
    setPushingId(factId);
    try {
      const res = await axios.post(`${apiBase}/admin/did-you-know/${factId}/send-push`, {}, {
        withCredentials: true,
        timeout: 120000,
      });
      toast.success(`Push sent to ${res.data?.recipients || 0} users`);
    } catch (e) {
      toast.error(`Push failed: ${e?.response?.data?.detail || 'unknown'}`);
    } finally {
      setPushingId(null);
    }
  };

  const handleTestPush = async () => {
    setTestingPush(true);
    try {
      const res = await axios.post(`${apiBase}/admin/did-you-know/test-push`, {}, {
        withCredentials: true,
        timeout: 30000,
      });
      const target = res.data?.target;
      const title = res.data?.fact_title;
      if (target === 'user') {
        toast.success(`Test push sent${title ? ` ("${title}")` : ''}`);
      } else if (target === 'fallback_segment') {
        toast.warning(res.data?.warning || 'Test push sent via fallback broadcast');
      } else {
        toast.success('Test push dispatched');
      }
    } catch (e) {
      toast.error(`Test push failed: ${e?.response?.data?.detail || 'unknown'}`);
    } finally {
      setTestingPush(false);
    }
  };

  if (!draft) {
    return (
      <AdminLayout>
        <div className="p-6 text-slate-400">Loading…</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
    <div className="max-w-5xl mx-auto" data-testid="admin-dyk-page">
      <div className="flex items-center gap-3 mb-6">
        <Sparkles className="h-6 w-6 text-amber-400" />
        <h1 className="text-2xl font-bold text-slate-100">Did You Know? — Admin</h1>
      </div>

      {/* Settings */}
      <section className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-100">Schedule</h2>
          <div className="flex items-center gap-2">
            {dirty && (
              <button
                onClick={handleRevert}
                className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-200 text-sm"
                data-testid="dyk-revert-btn"
              >
                Revert
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={!dirty || savingSettings}
              className="px-3 py-1.5 rounded-lg bg-amber-500 text-slate-900 text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              data-testid="dyk-save-btn"
            >
              {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {savingSettings ? 'Saving…' : (dirty ? 'Save changes' : 'Saved')}
            </button>
          </div>
        </div>

        {/* Schedule mode */}
        <div className="mb-5">
          <span className="text-sm text-slate-300 block mb-2">Cadence</span>
          <div className="flex gap-2 flex-wrap" data-testid="dyk-schedule-mode-row">
            {[
              { v: 'daily', label: 'Daily' },
              { v: 'weekly', label: 'Specific days' },
              { v: 'interval', label: 'Every N hours' },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => updateDraft({ schedule_mode: opt.v })}
                className={`px-3 py-2 rounded-lg text-sm font-medium ${draft.schedule_mode === opt.v ? 'bg-amber-500 text-slate-900' : 'bg-slate-800 text-slate-200'}`}
                data-testid={`dyk-mode-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Day-of-week picker (visible for daily/weekly) */}
        {(draft.schedule_mode === 'daily' || draft.schedule_mode === 'weekly') && (
          <div className="mb-5">
            <span className="text-sm text-slate-300 block mb-2">Days of the week</span>
            <div className="flex gap-2 flex-wrap" data-testid="dyk-weekday-picker">
              {WEEKDAY_LABELS.map((label, idx) => {
                const active = (draft.weekdays || []).includes(idx);
                return (
                  <button
                    key={label}
                    onClick={() => toggleWeekday(idx)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border ${active ? 'bg-amber-500 text-slate-900 border-amber-500' : 'bg-slate-800 text-slate-200 border-slate-700'}`}
                    data-testid={`dyk-weekday-${idx}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-500 mt-2">Tap to toggle. Daily = all 7 selected.</p>
          </div>
        )}

        {/* Interval (only for interval mode) */}
        {draft.schedule_mode === 'interval' && (
          <div className="mb-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm text-slate-300 block mb-1">Every N hours</span>
              <input
                type="number"
                min={1}
                max={168}
                value={draft.frequency_hours}
                onChange={(e) => updateDraft({ frequency_hours: parseInt(e.target.value || '24', 10) })}
                className="w-full bg-slate-800 text-slate-100 rounded-lg px-3 py-2 border border-slate-700"
                data-testid="dyk-frequency-input"
              />
              <span className="text-xs text-slate-500 mt-1 block">1 – 168 hours (1 hour to 1 week)</span>
            </label>
          </div>
        )}

        {/* Time + timezone */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          <label className="block">
            <span className="text-sm text-slate-300 block mb-1">Push time</span>
            <input
              type="time"
              value={`${String(draft.push_time_local_hour).padStart(2, '0')}:${String(draft.push_minute).padStart(2, '0')}`}
              onChange={(e) => {
                const [h, m] = (e.target.value || '09:00').split(':').map((n) => parseInt(n, 10));
                updateDraft({
                  push_time_local_hour: isNaN(h) ? 9 : h,
                  push_minute: isNaN(m) ? 0 : m,
                });
              }}
              className="w-full bg-slate-800 text-slate-100 rounded-lg px-3 py-2 border border-slate-700"
              data-testid="dyk-push-time-input"
            />
            <span className="text-xs text-slate-500 mt-1 block">Local to the timezone below</span>
          </label>
          <label className="block sm:col-span-2">
            <span className="text-sm text-slate-300 block mb-1">Timezone</span>
            <select
              value={draft.timezone}
              onChange={(e) => updateDraft({ timezone: e.target.value })}
              className="w-full bg-slate-800 text-slate-100 rounded-lg px-3 py-2 border border-slate-700"
              data-testid="dyk-timezone-select"
            >
              {timezones.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!draft.push_enabled}
            onChange={(e) => updateDraft({ push_enabled: e.target.checked })}
            data-testid="dyk-push-enabled-toggle"
            className="h-4 w-4"
          />
          <span className="text-sm text-slate-200">Send push notifications</span>
        </label>

        <div className="mt-5 flex flex-wrap gap-3 border-t border-slate-800 pt-4">
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            className="px-4 py-2 rounded-lg bg-amber-500 text-slate-900 font-medium flex items-center gap-2 disabled:opacity-60"
            data-testid="dyk-regenerate-btn"
          >
            {regenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {regenerating ? 'Generating…' : 'Generate new fact now'}
          </button>
          <button
            onClick={handleTestPush}
            disabled={testingPush}
            className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 font-medium flex items-center gap-2 disabled:opacity-60"
            data-testid="dyk-test-push-btn"
            title="Sends a push to your account only — useful for verifying the deep link, icon, and copy across your devices."
          >
            {testingPush ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
            {testingPush ? 'Sending…' : 'Send test push to me'}
          </button>
        </div>
      </section>

      {/* Filter + list */}
      <section className="bg-slate-900 border border-slate-800 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-100">Facts</h2>
          <div className="flex gap-2">
            {['published', 'archived', 'all'].map((s) => (
              <button
                key={s}
                onClick={() => { setFilter(s); fetchFacts(s); }}
                className={`px-3 py-1 text-sm rounded-lg ${filter === s ? 'bg-amber-500 text-slate-900' : 'bg-slate-800 text-slate-300'}`}
                data-testid={`dyk-filter-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        {loadingFacts ? (
          <p className="text-slate-400">Loading…</p>
        ) : facts.length === 0 ? (
          <p className="text-slate-400">No facts in this view.</p>
        ) : (
          <ul className="space-y-3" data-testid="dyk-facts-list">
            {facts.map((f) => (
              <li key={f.fact_id} className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-100 font-medium">{f.title}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {f.category?.replace(/_/g, ' ')} • {new Date(f.generated_at).toLocaleString()} • {f.status}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    {f.status === 'published' && (
                      <button
                        onClick={() => handlePush(f.fact_id)}
                        disabled={pushingId === f.fact_id}
                        className="px-2.5 py-1.5 rounded-lg bg-slate-700 text-slate-100 text-xs flex items-center gap-1 disabled:opacity-60"
                        data-testid={`dyk-send-push-${f.fact_id}`}
                      >
                        {pushingId === f.fact_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                        Push
                      </button>
                    )}
                    {f.status === 'published' ? (
                      <button
                        onClick={() => handleArchive(f.fact_id)}
                        className="px-2.5 py-1.5 rounded-lg bg-slate-700 text-slate-100 text-xs flex items-center gap-1"
                        data-testid={`dyk-archive-${f.fact_id}`}
                      >
                        <Archive className="h-3 w-3" />
                        Archive
                      </button>
                    ) : (
                      <button
                        onClick={() => handleRestore(f.fact_id)}
                        className="px-2.5 py-1.5 rounded-lg bg-slate-700 text-slate-100 text-xs flex items-center gap-1"
                        data-testid={`dyk-restore-${f.fact_id}`}
                      >
                        <Undo2 className="h-3 w-3" />
                        Restore
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-sm text-slate-300 line-clamp-3">{f.content}</p>
                {f.scripture_refs?.length > 0 && (
                  <p className="text-xs text-slate-500 mt-2">
                    Refs: {f.scripture_refs.join(', ')}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
    </AdminLayout>
  );
}
